import { css } from '@emotion/react';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import { faLockAlt } from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import {
  ButtonSize, ButtonStyle,
} from '~/_shared/baseComponents/buttons/button/button.types';
import { TextInputAuthComponent } from '~/_shared/baseComponents/inputs';
import { FormErrorMessageComponent } from '~/_shared/components/formErrorMessage/formErrorMessage.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { type FormField } from '~/authentication/authenticationFormField.types';
import { inputWrapperStyle } from '~/authentication/loginPage/loginForm/loginForm.component';
import { type UserLoginErrorCode } from '~/store/userData/repository/userLogin.types';
import { authorizationPagesErrorStyle } from '../../authenticationPages.styles';
import {
  AuthorizationButtonComponent, AuthorizationSubmitButtonComponent,
} from '../../submitButton/authenticationSubmitButton.component';
import { LoginFormLinksComponent } from './loginFormLinks.component';
import { RememberMeCheckboxComponent } from './rememberMeCheckbox.component';
import { UserLoginErrorComponent } from './userLoginError.component';

const backButtonWrapperStyle = css({ marginTop: 24 });

type LoginFormEmailViewProps = Readonly<{
  email: FormField;
  password: FormField;
  isLoading: boolean;
  rememberMe: boolean;
  showError: boolean;
  error: UserLoginErrorCode | string;
  disallowRegister?: boolean;
  onlyEmailLogin?: boolean;
  userEmail: string;
  onToggleRememberMe: () => void;
  onBack: () => void;
}>;

export const LoginFormEmailView: FC<LoginFormEmailViewProps> = (props) => {
  const [t] = useTranslation();

  return (
    <>
      <div css={inputWrapperStyle}>
        <TextInputAuthComponent
          autoComplete="username"
          hasError={props.email.errors.length > 0}
          icon={faEnvelope}
          isDisabled={props.isLoading}
          name="email"
          onBlur={props.email.onBlur}
          onChange={props.email.onChange}
          placeholder={t('Email')}
          type="email"
          value={props.email.value}
        />

        {props.email.errors.length > 0 && (
          <FormErrorMessageComponent
            css={authorizationPagesErrorStyle}
            messages={props.email.errors}
          />
        )}
      </div>

      <div css={inputWrapperStyle}>
        <TextInputAuthComponent
          autoComplete="current-password"
          icon={faLockAlt}
          name="password"
          placeholder={t('Password')}
          type="password"
          value={props.password.value}
          onChange={props.password.onChange}
          onBlur={props.password.onBlur}
          hasError={props.password.errors.length > 0}
          isDisabled={props.isLoading}
        />

        {props.password.errors.length > 0 && (
          <FormErrorMessageComponent
            css={authorizationPagesErrorStyle}
            messages={props.password.errors}
          />
        )}
      </div>

      <RememberMeCheckboxComponent
        isChecked={props.rememberMe}
        isDisabled={props.isLoading}
        onToggle={props.onToggleRememberMe}
      />

      <AuthorizationSubmitButtonComponent
        data-testid="login-submit"
        isLoading={props.isLoading}
      >
        {t('Sign in')}
      </AuthorizationSubmitButtonComponent>

      {props.showError && (
        <UserLoginErrorComponent
          code={props.error}
          userEmail={props.userEmail}
        />
      )}

      <LoginFormLinksComponent isLoading={props.isLoading} disallowRegister={props.disallowRegister} />

      {!props.onlyEmailLogin && (
        <div css={backButtonWrapperStyle}>
          <AuthorizationButtonComponent
            buttonStyle={ButtonStyle.Secondary}
            size={ButtonSize.Medium}
            isLoading={false}
            isDisabled={props.isLoading}
            onClick={props.onBack}
          >
            {t('login.email.signInAnotherWay')}
          </AuthorizationButtonComponent>
        </div>
      )}
    </>
  );
};
